/**
 * SimilarPlayerRowView - Individual player row in similar players list
 *
 * Displays player name, position, club, score badge,
 * and add-to-list button. Supports hover state and watchlist indicator.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { CheckIcon, PlusIcon } from "lucide-react-native";
import { memo } from "react";
import { GestureResponderEvent } from "react-native";

import BadgeComponent from "@/components/molecules/Badge";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import { SortField } from "@/generated/graphql";
import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/**
 * Props for SimilarPlayerRowView component
 */
interface SimilarPlayerRowViewProps {
  /** Whether the viewport is a mobile screen */
  readonly isMobile: boolean;
  /** Player's display name */
  readonly playerName: string;
  /** Formatted subtitle text (positions · club) */
  readonly subtitleText: string;
  /** Score value to display in badge, or null when the metric is unavailable */
  readonly score: number | null;
  /** Badge type for score display based on sort field */
  readonly badgeType: SortField;
  /** Minimum value for badge color scale */
  readonly badgeMin: number;
  /** Maximum value for badge color scale */
  readonly badgeMax: number;
  /** Whether higher values are better for badge color coding */
  readonly badgeIsHigherBetter: boolean;
  /** Whether to use neutral styling instead of color-coded styling */
  readonly badgeIsDefaultStyle?: boolean;
  /** Whether player is already on user's watchlist */
  readonly isOnWatchlist: boolean;
  /** Callback when add-to-list button is pressed (includes stopPropagation) */
  readonly onAddPress: (e: GestureResponderEvent) => void;
  /** Callback when row is pressed */
  readonly onRowPress: () => void;
}

/**
 * Renders a single player row in the similar players list.
 * Badge display adapts to the active sort field via dynamic min/max/style props.
 * @param props - Component props
 * @param props.isMobile - Whether the viewport is a mobile screen
 * @param props.playerName - Player's display name
 * @param props.subtitleText - Formatted subtitle text (positions · club)
 * @param props.score - Score value to display in badge
 * @param props.badgeType - Badge type for score display based on sort field
 * @param props.badgeMin - Minimum value for badge color scale
 * @param props.badgeMax - Maximum value for badge color scale
 * @param props.badgeIsHigherBetter - Whether higher values are better for badge color coding
 * @param props.badgeIsDefaultStyle - Whether to use neutral styling instead of color-coded styling
 * @param props.isOnWatchlist - Whether player is already on user's watchlist
 * @param props.onAddPress - Callback when add-to-list button is pressed (pre-wrapped with stopPropagation)
 * @param props.onRowPress - Callback when row is pressed
 * @returns Pressable row component
 */
const SimilarPlayerRowView = ({
  isMobile,
  playerName,
  subtitleText,
  score,
  badgeType,
  badgeMin,
  badgeMax,
  badgeIsHigherBetter,
  badgeIsDefaultStyle,
  isOnWatchlist,
  onAddPress,
  onRowPress,
}: SimilarPlayerRowViewProps) => (
  <Pressable
    onPress={onRowPress}
    accessibilityLabel={playerName}
    // SE-8285: `testID`, not a raw `data-testid` spread. The Pressable atom
    // forwards `testID` — that is the atom API contract, and react-native-web's
    // Pressable turns it into the DOM attribute — but an unknown `data-testid`
    // prop is dropped on the floor, silently. This row carried the spread form
    // and so matched ZERO elements while plainly on screen: SE-6219 found it
    // the hard way when a helper gated on the row container reported "no rows"
    // forever, and worked around it by addressing the name cell instead. The
    // two sibling ids below keep the spread because they sit on a `Text` and a
    // `Box`, both of which do reach the DOM — measured, not assumed.
    testID={TEST_ID_PLAYER_DETAIL.SIMILAR_PLAYER_ROW}
    className={
      isMobile
        ? "rounded-md border border-outline-card bg-surface-card p-4 active:bg-surface-muted"
        : "web:hover:bg-surface-muted px-4 py-3 active:bg-surface-muted"
    }
  >
    <Stack direction="horizontal" className="items-center gap-4">
      {/* Add to list button */}
      <Pressable
        onPress={onAddPress}
        className={`size-8 items-center justify-center rounded-full ${
          isOnWatchlist ? "bg-accent-primary" : "bg-surface-muted"
        }`}
      >
        <Icon
          as={isOnWatchlist ? CheckIcon : PlusIcon}
          size="sm"
          className={
            isOnWatchlist ? "text-content-on-accent" : "text-accent-primary"
          }
        />
      </Pressable>

      {/* Player info */}
      <Stack className="flex-1">
        <Text
          {...{ "data-testid": TEST_ID_PLAYER_DETAIL.SIMILAR_PLAYER_ROW_NAME }}
          variant="title-lg"
          className="font-heading text-content-primary"
          isTruncated
        >
          {playerName}
        </Text>
        <Text
          variant="title-sm"
          className="font-medium text-content-muted"
          isTruncated
        >
          {subtitleText}
        </Text>
      </Stack>

      {/* Score badge */}
      <Box
        {...{ "data-testid": TEST_ID_PLAYER_DETAIL.SP_COMMITTED_BADGE }}
        className="items-end"
      >
        <BadgeComponent
          type={badgeType}
          value={score}
          min={badgeMin}
          max={badgeMax}
          isHigherBetter={badgeIsHigherBetter}
          isDefaultStyle={badgeIsDefaultStyle}
        />
      </Box>
    </Stack>
  </Pressable>
);

SimilarPlayerRowView.displayName = "SimilarPlayerRowView";

export default memo(SimilarPlayerRowView);
